import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ExclamationCircleIcon } from '@heroicons/react/outline';
import IframeEmbed from '../IframeEmbed';
import { apiZedocGetMedicationConflictToken } from '../../../common/api/zedoc';
import Dialog from '../../Dialog';
import Button from '../../Button';

const STORAGE_KEY = 'zedoc:terms-accepted:medicationConflict';

const MedicationConflict = () => {
  const { t } = useTranslation();

  const [hasAccepted, setHasAccepted] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const [showDialog, setShowDialog] = useState(!hasAccepted);

  const handleAccept = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY, 'true');
    } catch {
      // Accept for this session even if localStorage is unavailable
    }
    setHasAccepted(true);
    setShowDialog(false);
  }, []);

  const handleDecline = useCallback(() => {
    setShowDialog(false);
  }, []);

  if (hasAccepted) {
    return (
      <IframeEmbed
        apiSpec={apiZedocGetMedicationConflictToken}
        params={{}}
        title="Medication Conflict Checker"
        toolName="medication conflict tool"
      />
    );
  }

  return (
    <>
      <Dialog
        data-testid="dialog-medication-conflict-terms"
        title={
          <span className="flex items-center gap-2">
            <ExclamationCircleIcon className="h-5 w-5 text-blue-600 flex-shrink-0" />
            {t('confirmations:medicationConflictTerms.title')}
          </span>
        }
        visible={showDialog}
        onCancel={handleDecline}
        size="large"
        footer={
          <>
            <Button
              data-testid="button-decline"
              type="secondary"
              onClick={handleDecline}
            >
              {t('confirmations:medicationConflictTerms.decline')}
            </Button>
            <Button
              data-testid="button-accept"
              type="primary"
              onClick={handleAccept}
            >
              {t('confirmations:medicationConflictTerms.accept')}
            </Button>
          </>
        }
      >
        <div className="stack-4">
          <p>{t('confirmations:medicationConflictTerms.betaDisclaimer')}</p>
          <p>{t('confirmations:medicationConflictTerms.clinicalDisclaimer')}</p>
          <p>{t('confirmations:medicationConflictTerms.securityNotice')}</p>
          <div className="bg-blue-50 dark:bg-blue-900 p-4 rounded-md border border-blue-200 dark:border-blue-700">
            <p>
              {t('confirmations:medicationConflictTerms.acceptanceStatement')}
            </p>
          </div>
        </div>
      </Dialog>
      {!showDialog && (
        <div className="flex flex-col justify-center items-center h-96">
          <p className="text-neutral-500">
            {t('confirmations:medicationConflictTerms.declinedMessage')}
          </p>
        </div>
      )}
    </>
  );
};

export default MedicationConflict;
